import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import OABDetailsModal from './OABDetailsModal';
import './PrecatorioGroupsMT.css';

const API_MTSE_URL = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_VERCEL_API_URL
  : process.env.REACT_APP_LOCAL_API_URL;

const PrecatorioGroupsMT = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('cpf');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOabGroup, setSelectedOabGroup] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    fetchData(activeTab);
  }, [activeTab]);

  const fetchData = async (type, forceUpdate = false) => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('sul_investimentos_token');
      const url = forceUpdate 
        ? `${API_MTSE_URL}/precatorio-groups/ba/${type}?forceUpdate=true`
        : `${API_MTSE_URL}/precatorio-groups/ba/${type}`;
        
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Erro ao buscar dados');
      }

      const result = await response.json();
      setData(result.data);
      setSummary(result.summary);
      
      // Show if data is cached
      if (result.cached) {
        const lastUpdate = new Date(result.lastUpdated);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBackClick = () => {
    navigate('/admin/precatorio-groups');
  };

  const toggleRowExpansion = (id) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRows(newExpanded);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatIdentifier = (identifier, type) => {
    if (!identifier) return '-';
    
    if (type === 'cpf') {
      // Format CPF: 123.456.789-00
      return identifier.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    } else if (type === 'cnpj') {
      // Format CNPJ: 12.345.678/0001-00
      return identifier.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }
    return identifier;
  };

  const getFilteredData = () => {
    if (!data || !searchTerm) return data;
    
    return data.filter(item => {
      const identifier = item[activeTab] || item.oab || '';
      const name = item.nome || item.razaoSocial || '';
      const searchLower = searchTerm.toLowerCase();
      
      return identifier.toLowerCase().includes(searchLower) ||
             name.toLowerCase().includes(searchLower);
    });
  };

  const filteredData = getFilteredData();

  const handleUpdateAll = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('sul_investimentos_token');
      const response = await fetch(`${API_MTSE_URL}/precatorio-groups/ba/update-all`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Erro ao atualizar dados');
      }

      // Force refresh current tab data
      await fetchData(activeTab, true);
    } catch (err) {
      console.error('Error updating data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOabClick = (oabGroup) => {
    setSelectedOabGroup(oabGroup);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedOabGroup(null);
  };

  const handleModalSave = async (updateData) => {
    try {
      const token = localStorage.getItem('sul_investimentos_token');
      const response = await fetch(`${API_MTSE_URL}/precatorio-groups/ba/oab/${encodeURIComponent(updateData.oab)}/update`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          nome: updateData.nome,
          lawyerStatus: updateData.lawyerStatus,
          comment: updateData.comment,
          precatorioStatuses: updateData.precatorioStatuses
        })
      });

      if (!response.ok) {
        throw new Error('Erro ao salvar alterações');
      }

      // Refresh data
      await fetchData(activeTab);
      
      // Close modal
      handleModalClose();
    } catch (err) {
      console.error('Error saving OAB group:', err);
      throw err;
    }
  };

  return (
    <div className="precatorio-groups-ba-container">
      <div className="header">
        <button className="back-button" onClick={handleBackClick}>
          ← Voltar
        </button>
        <h1>Agrupamento de Precatórios - Mato Grosso</h1>
        <button className="update-button" onClick={handleUpdateAll} disabled={loading}>
          🔄 Atualizar Dados
        </button>
      </div>

      <div className="tabs">
        <button
          className={`tab ${activeTab === 'cpf' ? 'active' : ''}`}
          onClick={() => setActiveTab('cpf')}
        >
          CPF
        </button>
        <button
          className={`tab ${activeTab === 'cnpj' ? 'active' : ''}`}
          onClick={() => setActiveTab('cnpj')}
        >
          CNPJ
        </button>
        <button
          className={`tab ${activeTab === 'oab' ? 'active' : ''}`}
          onClick={() => setActiveTab('oab')}
        >
          OAB
        </button>
      </div>

      {summary && (
        <div className="summary-cards">
          <div className="summary-card">
            <h3>Total de Grupos</h3>
            <p className="summary-value">{summary.totalGroups}</p>
          </div>
          <div className="summary-card">
            <h3>Total de Precatórios</h3>
            <p className="summary-value">{summary.totalPrecatorios}</p>
          </div>
          <div className="summary-card">
            <h3>Valor Total</h3>
            <p className="summary-value">{formatCurrency(summary.totalValue)}</p>
          </div>
          {activeTab === 'cpf' && (
            <div className="summary-card">
              <h3>Com E-mail</h3>
              <p className="summary-value">{summary.groupsWithEmail}</p>
            </div>
          )}
        </div>
      )}

      <div className="search-section">
        <input
          type="text"
          placeholder={`Buscar por ${activeTab === 'oab' ? 'OAB' : activeTab.toUpperCase()} ou nome...`}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
      </div>

      {loading ? (
        <div className="loading">Carregando...</div>
      ) : error ? (
        <div className="error">Erro: {error}</div>
      ) : filteredData && filteredData.length > 0 ? (
        <div className="data-table">
          <table>
            <thead>
              <tr>
                <th>{activeTab === 'oab' ? 'OAB' : activeTab.toUpperCase()}</th>
                <th>Nome</th>
                <th>Tipo</th>
                {activeTab === 'cpf' && <th>E-mail</th>}
                {activeTab === 'oab' && <th>Status</th>}
                <th>Qtd. Precatórios</th>
                <th>Valor Total</th>
                <th>Orçamento</th>
                <th>Ano Pagamento</th>
                {activeTab === 'oab' && <th>Modificado por</th>}
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.map((item) => (
                <React.Fragment key={item._id}>
                  <tr className={expandedRows.has(item._id) ? 'expanded' : ''}>
                    <td>
                      {activeTab === 'oab' 
                        ? item.oab 
                        : formatIdentifier(item[activeTab], activeTab)
                      }
                    </td>
                    <td>{item.nome || item.razaoSocial || 'N/A'}</td>
                    <td>{item.tipo || '-'}</td>
                    {activeTab === 'cpf' && <td>{item.email || '-'}</td>}
                    {activeTab === 'oab' && (
                      <td>
                        <span className={`status-badge status-${item.status?.replace(' ', '-')}`}>
                          {item.status || 'não contatado'}
                        </span>
                      </td>
                    )}
                    <td className="center">{item.totalCount}</td>
                    <td className="right">{formatCurrency(item.totalValue)}</td>
                    <td className="center">
                      {item.earliestOrcamento && item.latestOrcamento
                        ? item.earliestOrcamento === item.latestOrcamento
                          ? item.earliestOrcamento
                          : `${item.earliestOrcamento} - ${item.latestOrcamento}`
                        : '-'
                      }
                    </td>
                    <td className="center">
                      {item.earliestPaymentYear && item.latestPaymentYear
                        ? item.earliestPaymentYear === item.latestPaymentYear
                          ? item.earliestPaymentYear
                          : `${item.earliestPaymentYear} - ${item.latestPaymentYear}`
                        : '-'
                      }
                    </td>
                    {activeTab === 'oab' && (
                      <td className="center">
                        {item.statusUpdatedBy?.userName || '-'}
                      </td>
                    )}
                    <td className="center">
                      {activeTab === 'oab' ? (
                        <button
                          className="view-button"
                          onClick={() => handleOabClick(item)}
                        >
                          Ver Detalhes
                        </button>
                      ) : (
                        <button
                          className="expand-button"
                          onClick={() => toggleRowExpansion(item._id)}
                        >
                          {expandedRows.has(item._id) ? '−' : '+'}
                        </button>
                      )}
                    </td>
                  </tr>
                  {expandedRows.has(item._id) && activeTab !== 'oab' && (
                    <tr className="expanded-content">
                      <td colSpan={activeTab === 'cpf' ? 9 : 8}>
                        <div className="precatorios-detail">
                          <h4>Precatórios:</h4>
                          <table className="nested-table">
                            <thead>
                              <tr>
                                <th>Número</th>
                                <th>Natureza</th>
                                <th>Valor</th>
                                <th>Orçamento</th>
                                <th>Ano Pagamento</th>
                                <th>Tipo</th>
                                <th>Prioridade</th>
                              </tr>
                            </thead>
                            <tbody>
                              {item.precatorios.map((prec, index) => (
                                <tr key={index}>
                                  <td>{prec.numero_precatorio}</td>
                                  <td>{prec.natureza || '-'}</td>
                                  <td>{formatCurrency(prec.valor_atualizado)}</td>
                                  <td>{prec.orcamento || '-'}</td>
                                  <td>{prec.ano_pagamento_previsto || '-'}</td>
                                  <td>{prec.tipo || '-'}</td>
                                  <td>
                                    <span className={`badge ${prec.isPrioridade ? 'priority' : 'regular'}`}>
                                      {prec.isPrioridade ? 'Prioritário' : 'Regular'}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="no-data">Nenhum dado encontrado</div>
      )}
      
      {/* OAB Details Modal */}
      {activeTab === 'oab' && (
        <OABDetailsModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          oabGroup={selectedOabGroup}
          onSave={handleModalSave}
        />
      )}
    </div>
  );
};

export default PrecatorioGroupsMT;